"use client";

import React, { useState, useEffect } from "react";
import { Line, LineChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Area, AreaChart } from "recharts";
import { TrendingUp, Clock, Zap, ArrowUpRight, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const chartData = [{
  month: "Jan",
  engagement: 120,
  output: 45
}, {
  month: "Feb",
  engagement: 160,
  output: 70
}, {
  month: "Mar",
  engagement: 240,
  output: 110
}, {
  month: "Apr",
  engagement: 310,
  output: 190
}, {
  month: "May",
  engagement: 450,
  output: 280
}, {
  month: "Jun",
  engagement: 580,
  output: 420
}];
const chartConfig = {
  engagement: {
    label: "Engagement",
    color: "var(--chart-1)"
  },
  output: {
    label: "Content Volume",
    color: "var(--chart-2)"
  }
};
export default function MeasurableMomentum() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <section id="measurable-momentum" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes scanline {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(500%); opacity: 0; }
        }
        .scanline-bar {
          background: linear-gradient(to bottom, transparent, var(--primary), transparent);
          height: 20%;
          width: 100%;
          position: absolute;
          top: 0;
          left: 0;
          opacity: 0.1;
          animation: scanline 4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          pointer-events: none;
          z-index: 10;
        }
        .grid-bg {
          background-size: 40px 40px;
          background-image: linear-gradient(to right, var(--border) 1px, transparent 1px),
                            linear-gradient(to bottom, var(--border) 1px, transparent 1px);
          mask-image: linear-gradient(to bottom, transparent, black 40%, black 80%, transparent);
          opacity: 0.05;
        }
      `
    }} />

      {}
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Beschleunige&nbsp;<span className="text-primary" data-ast-id="f:/src/pages/Home/sections/MeasurableMomentum.tsx|sig:p8yc2l" data-ast-start="3130" data-ast-end="3174">Dein</span> Wachstum</h2>
            <p className="text-xl text-muted-foreground">Unsere KI-generierten Inhalt helfen Dir Kosten zu sparen und gleichzeitig Deine Marktwirkung zu steigern.</p>
          </div>
          <div className="flex gap-4">
             <Button asChild variant="outline" className="border-primary/20 hover:bg-primary/10 hover:text-primary">
                
             </Button>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[{
          label: "Kostenreduktion",
          value: "bis zu 60%",
          icon: TrendingUp,
          desc: "geringere Produktionskosten",
          color: "text-chart-1"
        }, {
          label: "Umsetzungszeit",
          value: "ab 48h",
          icon: Clock,
          desc: "vom Konzept bis Lieferung",
          color: "text-chart-2"
        }, {
          label: "Content-Volumen",
          value: "bis zu 10x",
          icon: Zap,
          desc: "mehr Content ohne Neueinstellung",
          color: "text-chart-4"
        }].map((metric, i) => <Card key={i} data-index={i} className="relative overflow-hidden border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300 group">
              <div className="scanline-bar" style={{
            animationDelay: `${i * 1.2}s`
          }} />
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    {metric.label}
                  </CardTitle>
                  <metric.icon className={`h-5 w-5 ${metric.color} opacity-80 group-hover:opacity-100 transition-opacity`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-5xl font-bold mb-2 ${metric.color} drop-shadow-sm`}>
                  {metric.value}
                </div>
                <CardDescription className="text-base">
                  {metric.desc}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>

        {}
        <Card className="border-border/50 bg-card/30 backdrop-blur-md overflow-hidden">
          <CardHeader className="border-b border-border/50 bg-card/20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Activity className="h-5 w-5 text-primary" />
                  Wachstum unserer Kunden
                </CardTitle>
                <CardDescription>6-Monats-Leistungsanalyse eines typischen KMU-Kunden</CardDescription>
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[var(--chart-1)]" />
                  <span>Engagement</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[var(--chart-2)]" />
                  <span>Volumen</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 pt-8">
            <div className="h-[350px] w-full">
              {mounted ? <ChartContainer config={chartConfig} className="h-full w-full">
                  <AreaChart data={chartData} margin={{
                top: 10,
                right: 10,
                left: 0,
                bottom: 0
              }}>
                    <defs>
                      <linearGradient id="fillEngagement" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-engagement)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-engagement)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillOutput" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-output)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-output)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.2} />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={10} stroke="var(--muted-foreground)" fontSize={12} />
                    <YAxis tickLine={false} axisLine={false} tickMargin={10} stroke="var(--muted-foreground)" fontSize={12} />
                    <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                    <Area dataKey="output" type="monotone" fill="url(#fillOutput)" fillOpacity={0.4} stroke="var(--color-output)" strokeWidth={2} stackId="1" />
                    <Area dataKey="engagement" type="monotone" fill="url(#fillEngagement)" fillOpacity={0.4} stroke="var(--color-engagement)" strokeWidth={2} stackId="2" />
                  </AreaChart>
                </ChartContainer> : <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                  Loading chart data...
                </div>}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}